import { useMemo, useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box, Prose, Text } from '@factorialco/deprecated-design-system'
import {
  AtsJobApplicationOverviewDocument,
  AtsJobApplicationOverviewFragment,
} from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { CandidateApplicationsList } from 'modules/ats/components/CandidateApplicationsList'
import { DuplicateCandidatesAlert } from 'modules/ats/components/DuplicateCandidatesAlert'
import { DuplicateCandidatesSidepanel } from 'modules/ats/components/DuplicateCandidatesSidepanel'
import { InterviewListing } from 'modules/ats/components/InterviewListing'

import { AISummary } from './AISummary'
import { AtsAnswerCard } from './AtsAnswerCard'

interface OutletContext {
  application: AtsJobApplicationOverviewFragment
}

const JobApplicationOverview = () => {
  const { application } = useOutletContext<OutletContext>()
  const { company } = useCurrentSession()
  const [isDuplicatesSidepanelOpen, setIsDuplicatesSidepanelOpen] = useState(false)
  const noData = !application.coverLetter && !application.answers?.length
  const { data, loading } = useQuery(AtsJobApplicationOverviewDocument, {
    variables: {
      atsApplicationId: application.id,
      atsCandidateId: application.candidate.id,
      companyId: company.id,
    },
  })

  const relatedApplications = data?.ats.relatedCandidateApplicationsConnection.nodes || []
  const applicationSummary = data?.ats.applicationSummariesConnection.nodes[0]

  const isDuplicateIdentificationEnabled =
    data?.features.duplicateIdentificationFeature.nodes[0]?.enabled ?? false

  const duplicates = useMemo(() => {
    const sourceDuplicates = data?.ats.source.nodes ?? []
    const matchedDuplicates = data?.ats.matched.nodes ?? []
    const allDuplicates = [...sourceDuplicates, ...matchedDuplicates]
    const dedupedDuplicates = new Map<number, (typeof allDuplicates)[number]>()

    for (const duplicate of allDuplicates) {
      dedupedDuplicates.set(duplicate.id, duplicate)
    }

    return Array.from(dedupedDuplicates.values())
  }, [data?.ats.source.nodes, data?.ats.matched.nodes])

  const hasDuplicates = isDuplicateIdentificationEnabled && duplicates.length > 0

  if (loading) {
    return null
  }

  return (
    <>
      <Box gap="s12">
        {hasDuplicates && (
          <DuplicateCandidatesAlert onView={() => setIsDuplicatesSidepanelOpen(true)} />
        )}
        <InterviewListing applicationId={application.id} />

        {!!applicationSummary && <AISummary data={applicationSummary} />}

        {noData && (
          <Box padding="s40" gap="s16" width="full" alignItems="center" textAlign="center">
            <Text.Normal>{i18n.t('ats.candidates.overview.candidate_profile.no_data')}</Text.Normal>
            <HelpCenterLink name="ats_create_offer">
              <Text.Normal weight="medium">
                {i18n.t('ats.candidates.overview.candidate_profile.help_link')}
              </Text.Normal>
            </HelpCenterLink>
          </Box>
        )}

        {application.coverLetter && (
          <Box gap="s12">
            <Text.Normal size="200" color="textSecondary" weight="medium">
              {i18n.t('ats.application_details.cover_letter_title')}
            </Text.Normal>
            <Box
              padding="s16"
              borderRadius={{ all: 'abs016' }}
              width="full"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              <Prose color="textPrimary" size="200" fullWidth>
                {application.coverLetter.replaceAll('\n', '<br />')}
              </Prose>
            </Box>
          </Box>
        )}

        {!!application.answers.length && (
          <Box gap="s12">
            <Text.Normal size="200" color="textSecondary" weight="medium">
              {i18n.t('ats.application_details.custom_questions_title')}
            </Text.Normal>
            <Box
              borderRadius={{ all: 'abs016' }}
              width="full"
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              {application.answers.map((answer, index) => (
                <AtsAnswerCard
                  key={answer.id}
                  answer={answer}
                  isLastItem={application.answers.length - 1 === index}
                />
              ))}
            </Box>
          </Box>
        )}

        <CandidateApplicationsList
          totalApplicationsCount={application.candidate.atsJobPostingIds?.length || 0}
          currentApplicationId={application.id}
          relatedApplications={relatedApplications}
          title={i18n.t('ats.application_details.other_applications')}
        />
      </Box>
      <Outlet context={{ application }} />

      {hasDuplicates && (
        <DuplicateCandidatesSidepanel
          open={isDuplicatesSidepanelOpen}
          onClose={() => setIsDuplicatesSidepanelOpen(false)}
          currentCandidate={application.candidate}
          jobPostingTitle={application.jobPosting?.title}
          duplicates={duplicates}
        />
      )}
    </>
  )
}

export default JobApplicationOverview
